/**
 * AdjustPackagesModal - Modal para ajustar paquetes
 *
 * Modal que permite al usuario ajustar la configuración de paquetes
 * durante el proceso de packing. Incluye:
 * - Configuración de cantidades por paquete
 * - Validación de cantidades máximas
 * - Integración con el flujo de packing
 * - Persistencia de ajustes en localStorage
 */

import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { useTranslation } from 'react-i18next';

// 🎯 Props para la modal de ajustar bultos
export interface AdjustPackagesModalProps {
  initialQuantity?: number;
  onCancel?: () => void;
  onConfirm?: (quantity: number) => void;
}

// 🎯 Referencia para controlar la modal
export interface AdjustPackagesModalRef {
  open: () => void;
  close: () => void;
}

// 🎯 Modal para ajustar cantidad de bultos
export const AdjustPackagesModal = forwardRef<AdjustPackagesModalRef, AdjustPackagesModalProps>(
  ({ initialQuantity = 1, onCancel, onConfirm }, ref) => {
    const modalRef = useRef<ModalCenterRef>(null);
    const { t } = useTranslation('packingModule');
    const MIN_BULTOS = 1;
    const MAX_BULTOS = 10;

    const clampBultos = (value: number) => Math.min(MAX_BULTOS, Math.max(MIN_BULTOS, value));

    const [quantity, setQuantity] = useState(() => clampBultos(initialQuantity));

    useImperativeHandle(ref, () => ({
      open: () => {
        setQuantity(clampBultos(initialQuantity));
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleQuantityChange = (newQuantity: number) => {
      setQuantity(clampBultos(newQuantity));
    };

    const handleCancel = () => {
      onCancel?.();
      modalRef.current?.closeModal();
    };

    const handleConfirm = () => {
      const safeQuantity = clampBultos(quantity);
      if (safeQuantity < MIN_BULTOS) return;
      onConfirm?.(safeQuantity);
      modalRef.current?.closeModal();
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modals.adjust_packages.title')}
        type="default"
        width="default"
        className="sm:max-w-[350px]"
      >
        <div className="space-y-6">
          {/* Descripción */}
          <p className="text-sm">{t('modals.adjust_packages.description')}</p>

          {/* Control de cantidad */}
          <div className="flex justify-center !mt-3">
            <StepperInput
              value={quantity}
              onChange={handleQuantityChange}
              min={MIN_BULTOS}
              max={MAX_BULTOS}
              step={1}
              allowNegative={false}
              className="w-full"
              data-testid="adjust-packages-stepper"
            />
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="lg" onClick={handleCancel} className="border-card-border">
              {t('modals.adjust_packages.cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleConfirm}
              className="bg-primary text-btn-text"
              disabled={quantity < MIN_BULTOS}
              data-testid="adjust-packages-confirm-button"
            >
              {t('modals.adjust_packages.confirm')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

AdjustPackagesModal.displayName = 'AdjustPackagesModal';
